import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, MapPin, BadgeCheck, FileText, CalendarClock, HelpCircle, Gavel, Briefcase, ShieldCheck, Mail } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import { whatsappLink } from "@/lib/faro";
import { useSeo, JsonLd, SITE_URL } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { LazyMap } from "@/components/LazyMap";
import { CalendarCourseJsonLd } from "@/components/CourseSchemaJsonLd";
import { AuthorityNote } from "@/components/AuthorityNote";
import { HorarioCarnet, HorarioLeyenda } from "@/components/HorarioCurso";
import CourseScheduleMedia from "@/components/CourseScheduleMedia";
import PointsRegistrationForm from "@/components/PointsRegistrationForm";
import TrustBarPuntos from "@/components/TrustBarPuntos";
import { CARNET_EUR, CARNET_EUROS } from "@/lib/courses";
import { usePricing } from "@/lib/usePricing";

const WA = whatsappLink("34637570075", "Hola, necesito recuperar el carnet (curso de 20 h en Vigo) y quiero que me orientéis sobre mi caso");
const AULA = "Centro Comercial 3 Plaza América, Local 12, 36211 Vigo";

const STATS = [
  { icon: Clock, k: "20 h", v: "3 días presenciales + 4 h online" },
  { icon: ShieldCheck, k: "8 puntos", v: "con los que recuperas el permiso si los perdiste todos" },
  { icon: FileText, k: "Con examen", v: "en Tráfico si perdiste la vigencia (B1 y B3)" },
  { icon: Gavel, k: "Judicial", v: "hasta 2 años: solo el curso, sin examen (B2)" },
];

const PASOS_B1 = [
  { n: "1", t: "Recibes la carta de pérdida de vigencia", d: "La DGT te notifica que tu saldo ha llegado a 0 puntos. Desde que la resolución es firme, no puedes conducir." },
  { n: "2", t: "Plazo de espera: 6 meses", d: "3 meses si eres conductor profesional. El plazo cuenta desde la pérdida de vigencia, no desde que hagas el curso." },
  { n: "3", t: "Curso de 20 horas", d: "3 jornadas presenciales en Plaza América + 4 h de teleformación. Puedes hacerlo durante la espera para no perder tiempo." },
  { n: "4", t: "Examen en la Jefatura de Tráfico", d: "Prueba de control de conocimientos tipo test. Tienes 3 convocatorias y te preparamos para aprobar a la primera." },
  { n: "5", t: "Vuelves a conducir con 8 puntos", d: "La Jefatura expide la nueva vigencia en días o pocas semanas. A los 2 años sin sanciones, subes a 12 puntos." },
];

const FAQ = [
  { q: "Me he quedado sin puntos, ¿qué tengo que hacer para recuperar el carnet?", a: "Tres pasos: esperar el plazo legal desde la pérdida de vigencia (6 meses, o 3 si eres conductor profesional), realizar el curso de recuperación de 20 horas en un centro autorizado por la DGT como el nuestro, y superar la prueba de control de conocimientos en la Jefatura de Tráfico. Nosotros te acompañamos en todo el proceso." },
  { q: "¿Qué es la carta de pérdida de vigencia?", a: "Es la resolución que la DGT te envía cuando tu saldo de puntos llega a 0. Desde que es firme, no puedes conducir. En ella figura la fecha a partir de la cual empieza a contar tu plazo de espera de 6 meses. Tráela a la matrícula si la tienes a mano: nos ayuda a calcular tus fechas exactas." },
  { q: "¿Cuánto tiempo tengo que esperar para recuperar el carnet si perdí los puntos?", a: "Puedes matricularte y hacer el curso cuando quieras, pero la DGT no te devolverá el permiso hasta que pasen 6 meses desde la pérdida de vigencia (3 meses si eres conductor profesional). Lo ideal es hacer el curso durante la espera para examinarte y recuperar el carnet justo al cumplirse el plazo." },
  { q: "¿Con cuántos puntos vuelvo a conducir?", a: "Recuperas el permiso con 8 puntos. Si pasas 2 años sin sanciones que resten puntos, tu saldo sube a 12." },
  { q: "¿Quién tiene que hacer el curso de sensibilización por sentencia judicial?", a: "Los conductores condenados por un delito contra la seguridad vial a la privación del derecho a conducir: alcoholemia penal, exceso de velocidad punible, conducción temeraria, negativa a someterse a las pruebas o conducir sin puntos. El curso de 20 horas es obligatorio para volver a conducir y solo puede impartirse en centros autorizados por la DGT como el nuestro." },
  { q: "Me retiraron el carnet por alcoholemia, ¿qué tengo que hacer?", a: "Depende de la duración de la privación que fije la sentencia. Si es de hasta 2 años (lo más habitual en alcoholemias), solo necesitas el curso de sensibilización de 20 horas: al cumplir la condena y con el curso aprovechado, recuperas tu permiso sin examen. Si supera los 2 años, además tendrás que superar de nuevo las pruebas de la DGT." },
  { q: "¿Cuál es la diferencia entre una retirada de hasta 2 años (B2) y de más de 2 años (B3)?", a: "Hasta 2 años (tipo B2): curso de 20 horas y sin examen; el permiso conserva su vigencia y lo recuperas al cumplir la condena. Más de 2 años (tipo B3): el permiso pierde su vigencia, por lo que además del curso debes superar otra vez las pruebas de la DGT (teórica y práctica)." },
  { q: "¿Tengo que esperar a cumplir la condena para hacer el curso?", a: "No. Puedes hacer el curso durante el cumplimiento de la condena. Es lo más recomendable: así, el mismo día en que se cumple la privación, tienes todo listo para volver a conducir sin perder ni una semana más." },
  { q: "¿Hay examen después del curso?", a: "Depende de tu caso. Si perdiste el carnet por quedarte a 0 puntos (B1), sí: una prueba de control de conocimientos tipo test en la Jefatura de Tráfico, y te la preparamos con nuestros test online. Si fue una retirada judicial de hasta 2 años (B2), no: al cumplir la condena recuperas tu permiso directamente. Si la privación superó los 2 años (B3), además del curso debes superar de nuevo las pruebas de la DGT." },
  { q: "¿Cuántas convocatorias tengo para aprobar el examen de Tráfico?", a: "Tres. Si suspendes o no te presentas, antes de la siguiente convocatoria debes realizar un ciclo adicional de 4 horas, que también impartimos nosotros de un día para otro. Si agotas las 3 convocatorias, hay que repetir el curso completo." },
  { q: "¿Puedo conducir mientras espero o durante la condena?", a: "No. Desde que la pérdida de vigencia es firme, o mientras dura la privación judicial, no puedes conducir ningún vehículo, tampoco por motivos laborales: hacerlo es un delito (en el caso judicial, quebrantamiento de condena). Lo que sí puedes es dejar el curso hecho cuanto antes. Si conduces por trabajo, te buscamos la convocatoria que mejor encaje con tus turnos para minimizar el tiempo sin permiso." },
  { q: "¿Cuándo recupero el permiso y cuánto tarda la DGT?", a: "Cuando se cumplan las condiciones de tu caso: en pérdida de puntos (B1), plazo de espera cumplido, curso aprovechado y prueba superada; en retirada judicial, condena cumplida y curso aprovechado (más la prueba teórica de la DGT si la privación superó los 2 años). Con todo en regla, la Jefatura tramita la nueva vigencia en días o pocas semanas. Nosotros gestionamos la comunicación con Tráfico." },
  { q: "¿Qué documentación tengo que llevar?", a: "DNI, NIE o permiso de conducir en vigor y, según tu caso, la resolución de la DGT de pérdida de vigencia o la sentencia judicial (o liquidación de condena) si la tienes a mano: con las fechas exactas te confirmamos cuándo podrás volver a conducir. Del resto del papeleo con Tráfico nos encargamos nosotros." },
  { q: "¿Cuánto cuesta el curso de 20 horas en Vigo?", a: `${CARNET_EUROS}, con las 20 horas (presencial + teleformación), el material, la preparación del examen cuando lo hay y la gestión con la DGT incluidos. Reserva por WhatsApp al 637 570 075 o llamando al 986 29 24 55.` },
  { q: "¿Dónde se imparte y cuánto dura?", a: `20 horas: 3 jornadas presenciales más 4 horas de sencilla teleformación online desde casa o desde la autoescuela. Lo impartimos en nuestro centro autorizado por la DGT: ${AULA}, con convocatorias todos los meses.` },
];

const SinPuntosSection = ({ onReserve }) => (
  <section className="mt-10 scroll-mt-24" id="sin-puntos">
    <h2 className="font-display font-black text-2xl sm:text-3xl">Me he quedado sin puntos (pérdida de vigencia)</h2>
    <p className="mt-3 text-[var(--faro-text-soft)]">
      Si tu saldo ha llegado a <strong>0 puntos</strong>, la DGT te envía la <strong>resolución de pérdida de
      vigencia</strong> del permiso. Desde que es firme no puedes conducir y empieza a contar tu plazo de espera.
      Este es el proceso completo (tipo B1):
    </p>
    <ol className="mt-4 space-y-3" data-testid="cs-pasos-b1">
      {PASOS_B1.map((p) => (
        <li key={p.n} className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5 flex gap-4">
          <span className="w-9 h-9 rounded-full bg-[var(--faro-red)] text-white font-display font-black flex items-center justify-center shrink-0">{p.n}</span>
          <div>
            <h3 className="font-display font-black text-lg leading-snug">{p.t}</h3>
            <p className="text-sm text-[var(--faro-text-soft)] mt-1">{p.d}</p>
          </div>
        </li>
      ))}
    </ol>

    <h3 className="font-display font-black text-xl mt-6">El examen en la Jefatura de Tráfico</h3>
    <p className="mt-2 text-[var(--faro-text-soft)]">
      Tras el curso, la ley exige superar una <strong>prueba de control de conocimientos</strong>: un test de{" "}
      <strong>30 preguntas</strong> con un máximo de <strong>3 errores</strong>. Dispones de <strong>3 convocatorias</strong>.
      Si suspendes o no te presentas, antes de la siguiente tendrás que hacer un{" "}
      <Link to="/ciclo-adicional-vigo" className="text-[var(--faro-blue)] font-bold underline underline-offset-2" data-testid="cs-link-ciclo">ciclo adicional de 4 horas</Link>{" "}
      (lo impartimos individual y de un día para otro).
    </p>

    <h3 className="font-display font-black text-xl mt-6">Plazos de la DGT</h3>
    <ul className="mt-2 space-y-2 text-[var(--faro-text-soft)]">
      <li className="flex gap-2"><Mail size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Espera mínima de 6 meses</strong> desde la pérdida de vigencia (<strong>3 meses</strong> para conductores profesionales). Haz el curso durante la espera para no perder ni un día.</span></li>
      <li className="flex gap-2"><BadgeCheck size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Tras el curso y la prueba teórica</strong>, la Jefatura tramita la nueva vigencia en <strong>días o pocas semanas</strong>.</span></li>
      <li className="flex gap-2"><ShieldCheck size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span>Vuelves a conducir con <strong>8 puntos</strong>, que pasan a <strong>12 tras 2 años</strong> sin sanciones.</span></li>
    </ul>
    <div className="mt-5">
      <a href="#reserva" onClick={onReserve("B1")} className="btn-pill btn-primary text-sm" data-testid="cs-cta-reserva-b1">
        <CalendarClock size={15} /> Reserva plaza ya
      </a>
    </div>
  </section>
);

const RetiradaJudicialSection = ({ onReserve }) => (
  <section className="mt-10 scroll-mt-24" id="retirada-judicial">
    <h2 className="font-display font-black text-2xl sm:text-3xl">Me han retirado el carnet por sentencia judicial</h2>
    <p className="mt-3 text-[var(--faro-text-soft)]">
      Cuando la sentencia por un <strong>delito contra la seguridad vial</strong> incluye la privación del derecho a
      conducir, el curso de 20 horas es obligatorio. Los casos más habituales que atendemos en Vigo:
    </p>
    <ul className="mt-3 list-disc pl-5 space-y-1 text-[var(--faro-text-soft)]">
      <li><strong>Alcoholemia penal:</strong> superar 0,60 mg/l en aire espirado (1,2 g/l en sangre) o conducir bajo la influencia del alcohol o las drogas.</li>
      <li><strong>Exceso de velocidad punible:</strong> superar en más de 60 km/h en vía urbana o 80 km/h en interurbana la velocidad permitida.</li>
      <li><strong>Conducción temeraria</strong> o con manifiesto desprecio por la vida de los demás.</li>
      <li><strong>Negativa a someterse</strong> a las pruebas de alcohol o drogas.</li>
      <li><strong>Conducir sin puntos</strong> o con el permiso retirado.</li>
    </ul>

    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--faro-red)]">Tipo B2</div>
        <h3 className="font-display font-black text-xl mt-1">Privación de hasta 2 años</h3>
        <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-[var(--faro-text-soft)]">
          <li>Es el caso más frecuente (alcoholemias y velocidad).</li>
          <li>Tu permiso <strong>conserva la vigencia</strong>: no hay que examinarse de nada.</li>
          <li>Realizas el curso de 20 horas, puedes hacerlo <strong>durante la condena</strong>.</li>
          <li>Al cumplir la privación y con el curso aprovechado, <strong>recuperas tu permiso</strong>.</li>
        </ul>
      </div>
      <div className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] p-5">
        <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--faro-red)]">Tipo B3</div>
        <h3 className="font-display font-black text-xl mt-1">Privación superior a 2 años</h3>
        <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-[var(--faro-text-soft)]">
          <li>El permiso <strong>pierde su vigencia</strong> por la duración de la condena.</li>
          <li>Realizas el mismo curso de 20 horas…</li>
          <li>…y además debes superar la prueba te&oacute;rica de la DGT</li>
          <li>Te preparamos el curso y las pruebas en el mismo centro.</li>
        </ul>
      </div>
    </div>

    <ul className="mt-5 space-y-2 text-[var(--faro-text-soft)]">
      <li className="flex gap-2"><Gavel size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Durante la condena:</strong> no puedes conducir ningún vehículo (hacerlo es quebrantamiento de condena), pero <strong>sí puedes hacer el curso</strong>.</span></li>
      <li className="flex gap-2"><BadgeCheck size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Lo recomendable:</strong> matricúlate cuanto antes y deja el curso hecho. Así vuelves a conducir el mismo día en que se cumple la privación.</span></li>
      <li className="flex gap-2"><Briefcase size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Si conduces por trabajo:</strong> te buscamos la convocatoria que mejor encaje con tus turnos para minimizar el tiempo sin permiso.</span></li>
    </ul>
    <div className="mt-5">
      <a href="#reserva" onClick={onReserve("B2")} className="btn-pill btn-primary text-sm" data-testid="cs-cta-reserva-b2">
        <CalendarClock size={15} /> Reserva plaza ya
      </a>
    </div>
  </section>
);

const hashToType = (h) => (h === "#sin-puntos" ? "B1" : h === "#retirada-judicial" ? "B2" : "");

export default function CursoSensibilizacionPage() {
  const { carnetEur } = usePricing();
  const priceCarnet = carnetEur || CARNET_EUR;
  const [ctx, setCtx] = useState(() => {
    const t = typeof window !== "undefined" ? hashToType(window.location.hash) : "";
    return t ? { v: t } : null;
  });
  const reserveFrom = (t) => () => setCtx({ v: t });

  useEffect(() => {
    const onHash = () => {
      const t = hashToType(window.location.hash);
      if (t) setCtx({ v: t });
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useSeo({
    title: "Recuperar el Carnet en Vigo · Curso 20 h · Centro DGT",
    description: `Curso oficial de 20 h para recuperar el carnet en Vigo, por pérdida de puntos o retirada judicial. ${CARNET_EUR}, centro autorizado DGT en Plaza América.`,
    canonical: `${SITE_URL}/curso-sensibilizacion-vigo`,
    ogImage: "/assets/og/curso-sensibilizacion-vigo.jpg",
    ogImageAlt: "Curso de sensibilización y reeducación vial de 20 horas en Vigo",
  });

  return (
    <div className="pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-0" data-testid="curso-sensibilizacion-page">
      <CalendarCourseJsonLd tipo="carnet" />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }} />

      <Header />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-1">
        <Breadcrumbs items={[{ label: "Permiso por puntos", to: "/permiso-por-puntos-vigo" }, { label: "Curso de sensibilización (20 h)" }]} />
      </div>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        <span className="eyebrow">Centro autorizado DGT en Vigo</span>
        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-3 leading-tight">
          Curso de sensibilización y reeducación vial en Vigo: recupera tu carnet
        </h1>
        <p className="mt-4 text-lg text-[var(--faro-text-soft)]">
          El <strong>curso de 20 horas</strong> que la ley exige para volver a conducir, tanto si te has quedado{" "}
          <strong>sin puntos</strong> (pérdida de vigencia) como si un juez te ha <strong>retirado el carnet</strong> por
          alcoholemia, velocidad o conducción temeraria. Lo impartimos en nuestro centro autorizado por la DGT de
          Plaza América y te decimos exactamente qué necesitas según tu caso.
        </p>

        <aside
          className="mt-6 rounded-2xl bg-white border-2 border-l-8 p-4 sm:p-5"
          style={{ borderColor: "#161692", borderLeftColor: "#DE0000" }}
          data-testid="dgt-credential-box"
        >
          <h2 className="font-display font-black text-lg sm:text-xl leading-tight" style={{ color: "#161692" }}>
            Centro autorizado por la DGT
          </h2>
          <p className="mt-2 text-sm sm:text-base text-[var(--faro-text)]">
            Autoescuelas Faro es centro de formación autorizado por la Dirección General de Tráfico con el número 360024. Impartimos cursos de recuperación de puntos desde 2018.
          </p>
        </aside>

        <div className="mt-6 flex flex-wrap gap-3">
          <a href="#reserva" className="btn-pill btn-primary text-sm" data-testid="cs-cta-reserva">
            <CalendarClock size={15} /> Reserva tu plaza
          </a>
          <a href={WA} target="_blank" rel="noreferrer" className="btn-pill btn-outline text-sm" data-testid="cs-cta-whatsapp">
            Cuéntanos tu caso por WhatsApp
          </a>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {STATS.map((f) => (
            <div key={f.k} className="rounded-2xl bg-[var(--faro-blue)] text-white p-4">
              <f.icon size={18} className="text-[var(--faro-yellow)]" />
              <div className="font-display font-black text-2xl mt-2">{f.k}</div>
              <div className="text-xs text-white/80 mt-0.5 leading-snug">{f.v}</div>
            </div>
          ))}
        </div>

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Dos situaciones, el mismo curso de 20 horas</h2>
          <p className="mt-3 text-[var(--faro-text-soft)]">
            Aunque se llegue por caminos distintos, la formación es la misma: el curso de sensibilización y reeducación
            vial de 20 horas regulado por la DGT. <strong>Ambos perfiles comparten aula</strong> en las mismas
            convocatorias; lo que cambia es lo que ocurre después del curso:
          </p>
          <ul className="mt-3 list-disc pl-5 space-y-1 text-[var(--faro-text-soft)]">
            <li><a href="#sin-puntos" className="text-[var(--faro-blue)] font-bold underline underline-offset-2" data-testid="cs-link-sin-puntos">Me he quedado sin puntos</a>: proceso administrativo con la DGT, con examen en Tráfico (tipo B1).</li>
            <li><a href="#retirada-judicial" className="text-[var(--faro-blue)] font-bold underline underline-offset-2" data-testid="cs-link-judicial">Me han retirado el carnet por sentencia judicial</a>: hasta 2 años sin examen (B2); más de 2 años con pruebas de la DGT (B3).</li>
          </ul>
        </section>

        <SinPuntosSection onReserve={reserveFrom} />

        <RetiradaJudicialSection onReserve={reserveFrom} />

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Duración y desglose del curso</h2>
          <ul className="mt-3 space-y-2 text-[var(--faro-text-soft)]">
            <li className="flex gap-2"><Clock size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Parte presencial:</strong> 3 jornadas en el aula de Plaza América (Vigo): viernes, sábado y el viernes de la semana siguiente, con horarios compatibles con el trabajo.</span></li>
            <li className="flex gap-2"><FileText size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Parte de teleformación:</strong> 4 horas online que completas a tu ritmo hasta las 20 h totales.</span></li>
            <li className="flex gap-2"><BadgeCheck size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" /><span><strong>Certificado de aprovechamiento:</strong> al terminar, comunicamos tu apto a Tráfico para que no haya demoras.</span></li>
          </ul>
          <div className="mt-4" data-testid="cs-horario">
            <HorarioCarnet />
            <HorarioLeyenda />
          </div>
          <h3 className="font-display font-black text-xl mt-6">Temario y estructura del curso oficial</h3>
          <p className="mt-2 text-[var(--faro-text-soft)]">
            El curso está regulado por la DGT (Orden INT/914/2024) y solo pueden impartirlo centros autorizados.
            Sus 20 horas se organizan así:
          </p>
          <ul className="mt-3 list-disc pl-5 space-y-1 text-[var(--faro-text-soft)]">
            <li><strong>Bloques formativos con profesor:</strong> factores de riesgo (alcohol, drogas, velocidad, distracciones), normativa y consecuencias penales de los delitos viales.</li>
            <li><strong>Sesión con psicólogo:</strong> trabajo en grupo sobre actitudes al volante y gestión de la conducta de riesgo.</li>
            <li><strong>Testimonio de una víctima</strong> de accidente de tráfico, la parte que más marca a los alumnos.</li>
            <li><strong>4 horas de teleformación:</strong> las completas online a tu ritmo hasta cerrar las 20 horas.</li>
          </ul>
        </section>

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Precio</h2>
          <p className="mt-3 text-[var(--faro-text-soft)]">
            El curso de 20 horas cuesta <strong className="text-[var(--faro-blue-deep)] text-xl">{priceCarnet}</strong>, con
            las 20 horas, el material, la preparación del examen cuando lo hay y la gestión con la DGT incluidos. Cuéntanos tu caso por{" "}
            <a href={WA} target="_blank" rel="noreferrer" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">WhatsApp (637 570 075)</a>{" "}
            o llamando al <a href="tel:+34986292455" className="text-[var(--faro-blue)] font-bold underline underline-offset-2">986 29 24 55</a>: con tu resolución o sentencia te confirmamos al momento qué necesitas y las fechas.
          </p>
        </section>

        <CourseScheduleMedia variant="carnet" hideChart />

        <div className="mt-10">
          <TrustBarPuntos />
        </div>

        <section className="mt-10 scroll-mt-24" id="reserva" data-testid="cs-inscripcion">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Reserva tu plaza en el curso de 20 horas</h2>
          <p className="mt-3 text-[var(--faro-text-soft)]">
            Marca tu situación, deja tus datos y te llamamos para confirmar tu plaza. Si no tienes claro tu caso,
            elige «No lo sé» y te orientamos sin compromiso.
          </p>
          <div className="mt-5">
            <PointsRegistrationForm
              cardOptions={["B1", "B2", "B3", "NO_SABE"]}
              contextType={ctx}
              formId="sensibilizacion-formulario"
              sideTitle="Reserva tu curso de 20 horas"
            />
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Dónde se imparte</h2>
          <p className="mt-3 text-[var(--faro-text-soft)] flex items-start gap-2">
            <MapPin size={18} className="text-[var(--faro-red)] shrink-0 mt-0.5" />
            <span>
              <strong>{AULA}</strong> — nuestro{" "}
              <Link to="/autoescuela-plaza-america" className="text-[var(--faro-blue)] font-bold underline underline-offset-2" data-testid="cs-link-aula">
                centro autorizado por la DGT en Plaza América (Vigo)
              </Link>. También atendemos matrículas en Coruxo, Panxón, Nigrán y A Cañiza.
            </span>
          </p>
          <div className="mt-4 rounded-2xl relative overflow-hidden border border-[rgba(30,42,143,0.12)] h-64">
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("Autoescuelas Faro, Centro Comercial 3 Plaza América, Vigo")}`} target="_blank" rel="noopener noreferrer" className="block w-full h-full" aria-label="Abrir la ubicacion en Google Maps"><img src="/img/mapa-plaza-america.jpg" alt="Mapa de la zona de Plaza America en Vigo" width="1024" height="420" className="w-full h-full object-cover" /></a><span className="pointer-events-none absolute left-2 top-2 rounded bg-white px-3 py-1.5 text-xs font-medium text-[#202124] shadow">Abrir en Maps &#8599;</span><div className="pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-3 -translate-y-full items-end gap-1"><svg width="28" height="36" viewBox="0 0 24 32" aria-hidden="true"><path d="M12 0C5.4 0 0 5.3 0 12c0 8.9 12 20 12 20s12-11.1 12-20C24 5.3 18.6 0 12 0z" fill="#EA4335" /><circle cx="12" cy="12" r="4.5" fill="#fff" /></svg><span className="mb-1 rounded bg-white/95 px-2 py-1 text-[11px] font-medium text-[#202124] shadow">Autoescuelas Faro - Plaza América</span></div><span className="absolute bottom-1 right-2 text-[10px] bg-white/80 px-1 rounded">&copy; OpenStreetMap contributors</span>
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Qué tienes que llevar</h2>
          <ul className="mt-3 list-disc pl-5 space-y-1 text-[var(--faro-text-soft)]">
            <li><strong>DNI, NIE o permiso de conducir en vigor.</strong></li>
            <li><strong>La resolución de la DGT</strong> de pérdida de vigencia <strong>o la sentencia judicial</strong> (o liquidación de condena), si la tienes a mano: con las fechas exactas te confirmamos cuándo podrás volver a conducir.</li>
            <li>Del resto del papeleo con Tráfico <strong>nos encargamos nosotros</strong>.</li>
          </ul>
        </section>

        <section className="mt-10" id="faq">
          <h2 className="font-display font-black text-2xl sm:text-3xl flex items-center gap-2">
            <HelpCircle className="text-[var(--faro-red)]" size={26} /> Preguntas frecuentes
          </h2>
          <div className="mt-4 space-y-2" data-testid="cs-faq">
            {FAQ.map((f) => (
              <details key={f.q} className="rounded-2xl bg-white border border-[rgba(30,42,143,0.12)] px-5 py-4 group">
                <summary className="cursor-pointer list-none font-bold text-[var(--faro-blue-deep)] flex items-center justify-between gap-3">
                  {f.q}
                  <span className="text-[var(--faro-red)] group-open:rotate-90 transition-transform shrink-0"><ArrowRight size={16} /></span>
                </summary>
                <p className="mt-2 text-sm text-[var(--faro-text-soft)]">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-display font-black text-2xl sm:text-3xl">¿No es tu caso?</h2>
          <ul className="mt-3 list-disc pl-5 space-y-1 text-[var(--faro-text-soft)]">
            <li>Si <strong>conservas el carnet</strong> pero has perdido parte de tus puntos, lo tuyo es el{" "}
              <Link to="/recuperacion-de-puntos-vigo" className="text-[var(--faro-blue)] font-bold underline underline-offset-2" data-testid="cs-link-puntos">curso de recuperación de puntos (1 día presencial + 3 h online)</Link>.
            </li>
            <li>Si ya hiciste el curso y <strong>suspendiste la prueba de Tráfico</strong>, necesitas el{" "}
              <Link to="/ciclo-adicional-vigo" className="text-[var(--faro-blue)] font-bold underline underline-offset-2" data-testid="cs-link-ciclo-2">ciclo adicional de 4 h</Link>.
            </li>
          </ul>
        </section>

        <AuthorityNote />

        <p className="mt-8 text-sm text-[var(--faro-text-soft)]" data-testid="cs-cambios-dgt-link">
          Si vuelves a examinarte, ten en cuenta los{" "}
          <Link to="/blog/cambios-dgt-1-octubre-2026" className="font-bold text-[var(--faro-blue)] underline underline-offset-2">
            cambios de la DGT del 1 de octubre de 2026
          </Link>
          : el banco de preguntas del teórico se actualiza con la nueva normativa.
        </p>

        <div className="mt-10 rounded-2xl bg-[var(--faro-blue)] text-white p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[var(--faro-yellow)]">Convocatorias cada mes</div>
            <div className="font-display font-black text-xl mt-1">Deja el curso hecho y no pierdas ni un día</div>
            <p className="text-sm text-white/85 mt-1">Curso de sensibilización y reeducación vial en Vigo · Centro autorizado DGT desde 1980.</p>
          </div>
          <div className="flex flex-col gap-2">
            <a href="#reserva" className="btn-pill btn-primary text-sm justify-center" data-testid="cs-cta-final">
              <CalendarClock size={14} /> Reserva tu plaza
            </a>
            <a href={WA} target="_blank" rel="noreferrer" className="btn-pill bg-white text-[var(--faro-blue)] text-sm justify-center">
              WhatsApp <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </main>

      <Footer />
      <FloatingWhatsapp />
      <MobileBottomNav />
    </div>
  );
}
